import type { ReactNode } from "react";
import { Route } from "wouter";

import { Guarded } from "@/app/guards";
import {
  ChallengesPage, ChallengeRespondPage, ChallengeThreadPage, CommRecordScreen, ComposePage,
  ConversationThreadPage, ConversationsNewEntryPage, ConversationsPage, GroupConversationCreatePage,
  GroupConversationThreadPage, InternalConversationCreatePage, IssuedChallengesPage, NewChallengePage,
  NewPaymentRequestPage, NewReverseChallengePage, RetrospectivePage, WorkflowDetailPage, WorkflowListPage,
  WorkflowNewPage,
} from "@/app/lazy-pages";
import { AppShellLayout } from "@/components/layout/app-shell";

import type { CurrentUser } from "@/app/app-routes";

interface ChallengeRouteContext { user: CurrentUser | null; loading: boolean; }

function protectedPage(context: ChallengeRouteContext, page: ReactNode, shell = true): JSX.Element {
  const content = shell ? <AppShellLayout displayName={context.user?.displayName} aal={context.user?.aal}>{page}</AppShellLayout> : page;
  return <Guarded user={context.user} loading={context.loading} minAal="AAL2">{content}</Guarded>;
}

/** Build challenge routes as direct children for Wouter's outer Switch. */
export function buildChallengeRoutes(context: ChallengeRouteContext): JSX.Element {
  return <>
    <Route path="/challenges/:id/respond">{protectedPage(context, <ChallengeRespondPage />)}</Route>
    <Route path="/challenges/:id/thread">{protectedPage(context, <ChallengeThreadPage />)}</Route>
    <Route path="/challenges/:id/retrospective">{protectedPage(context, <RetrospectivePage />)}</Route>
    <Route path="/challenges/new-reverse">{protectedPage(context, <NewReverseChallengePage />, false)}</Route>
    <Route path="/challenges/issued">{protectedPage(context, <IssuedChallengesPage />)}</Route>
    <Route path="/challenges/new-payment-request">{protectedPage(context, <NewPaymentRequestPage />, false)}</Route>
    <Route path="/challenges/new">{protectedPage(context, <NewChallengePage />, false)}</Route>
    <Route path="/challenges/workflows/new">{protectedPage(context, <WorkflowNewPage userId={context.user?.id ?? ""} />, false)}</Route>
    <Route path="/challenges/workflows/:id">{protectedPage(context, <WorkflowDetailPage viewerUserId={context.user?.id ?? ""} />)}</Route>
    <Route path="/challenges/workflows">{protectedPage(context, <WorkflowListPage viewerUserId={context.user?.id ?? ""} />)}</Route>
    <Route path="/challenges/compose">{protectedPage(context, <ComposePage />, false)}</Route>
    <Route path="/challenges/conversations/new">{protectedPage(context, <ConversationsNewEntryPage />)}</Route>
    <Route path="/challenges/conversations/group/new">{protectedPage(context, <GroupConversationCreatePage />, false)}</Route>
    <Route path="/challenges/conversations/group/:conversationId">
      {(params) => protectedPage(context, <GroupConversationThreadPage conversationId={params.conversationId} />)}
    </Route>
    {/* Internal Team Conversations, module 03 (D8): "new" MUST be registered
        before the ":conversationId" route below, mirroring group/new above —
        otherwise "new" is swallowed as an id. Reuses GroupConversationThreadPage
        with scope="internal" rather than a forked thread component. */}
    <Route path="/challenges/conversations/internal/new">{protectedPage(context, <InternalConversationCreatePage />, false)}</Route>
    <Route path="/challenges/conversations/internal/:conversationId">
      {(params) => protectedPage(context, <GroupConversationThreadPage conversationId={params.conversationId} scope="internal" />)}
    </Route>
    <Route path="/challenges/conversations/:counterpartyOrgId">
      {(params) => protectedPage(context, <ConversationThreadPage counterpartyOrgId={params.counterpartyOrgId} />)}
    </Route>
    <Route path="/challenges/conversations">{protectedPage(context, <ConversationsPage />)}</Route>
    <Route path="/challenges/record/:communicationId">{protectedPage(context, <CommRecordScreen />)}</Route>
    <Route path="/challenges">{protectedPage(context, <ChallengesPage />)}</Route>
  </>;
}
